import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FileText, HelpCircle, Building, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { BlogManager, FaqManager, SectorManager } from '@/components/cms';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentManagementPage = () => {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const isSpanish = i18n.language === 'es';
  
  const [activeTab, setActiveTab] = useState('blog');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data states
  const [blogPosts, setBlogPosts] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [sectors, setSectors] = useState([]);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {};
      
      const [blogRes, faqRes, sectorsRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/blog?published_only=false`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/cms/faq?published_only=false`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/cms/sectors?published_only=false`, { headers, credentials: 'include' })
      ]);
      
      if (blogRes.ok) setBlogPosts(await blogRes.json());
      if (faqRes.ok) setFaqItems(await faqRes.json());
      if (sectorsRes.ok) setSectors(await sectorsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(isSpanish ? 'Error al cargar datos' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [isSpanish]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated, fetchAllData]);

  // Filter data by search query
  const filteredBlogPosts = blogPosts.filter(p => 
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFaqItems = faqItems.filter(f => 
    f.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSectors = sectors.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Auth checks
  if (authLoading) {
    return (
      <div className="min-h-screen bg-steel-50 pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-steel-50 pt-24" data-testid="content-management-page">
      {/* Header */}
      <section className="bg-navy-900 py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
                {isSpanish ? 'Gestión de Contenido' : 'Content Management'}
              </h1>
              <p className="text-steel-300">
                {isSpanish 
                  ? 'Administra artículos del blog, FAQs y sectores'
                  : 'Manage blog posts, FAQs, and industry sectors'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={isSpanish ? 'Buscar...' : 'Search...'}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-steel-400 w-64"
                />
              </div>
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={fetchAllData}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8">
        <div className="container-custom">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="blog" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Blog ({blogPosts.length})
              </TabsTrigger>
              <TabsTrigger value="faq" className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                FAQ ({faqItems.length})
              </TabsTrigger>
              <TabsTrigger value="sectors" className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                {isSpanish ? 'Sectores' : 'Sectors'} ({sectors.length})
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
                <p className="text-steel-600">{isSpanish ? 'Cargando...' : 'Loading...'}</p>
              </div>
            ) : (
              <>
                <TabsContent value="blog">
                  <BlogManager 
                    posts={filteredBlogPosts} 
                    onRefresh={fetchAllData} 
                    isSpanish={isSpanish} 
                  />
                </TabsContent>

                <TabsContent value="faq">
                  <FaqManager 
                    faqs={filteredFaqItems} 
                    onRefresh={fetchAllData} 
                    isSpanish={isSpanish} 
                  />
                </TabsContent>

                <TabsContent value="sectors">
                  <SectorManager 
                    sectors={filteredSectors} 
                    onRefresh={fetchAllData} 
                    isSpanish={isSpanish} 
                  />
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default ContentManagementPage;
